// Shared date-picker helpers used by both Manday.jsx (day-based enquiries) and
// Manmonth.jsx (month-based enquiries), so the two pages never silently diverge
// on locking/shift-range/day-math behavior.

// Expands an inclusive [startStr, endStr] range (YYYY-MM-DD strings, either order)
// into every YYYY-MM-DD day in between. Used for the day-picker's shift-click range select.
export const expandDateRange = (startStr, endStr) => {
  const [a, b] = [new Date(startStr), new Date(endStr)].sort((x, y) => x - y);
  const out = [];
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

// react-multi-date-picker `mapDays` factory: locks already-invoiced/assigned days
// (disabled, styled, with a title), greys out disallowed days, and supports
// shift-click range selection for the rest.
export const makeShiftRangeMapDays = ({ getValues, setValues, anchorRef, isAllowed, isLocked, lockedTitle, lockedStyle }) =>
  ({ date }) => {
    const dateStr = date.format("YYYY-MM-DD");
    if (isLocked && isLocked(dateStr)) {
      return {
        disabled: true,
        style: lockedStyle !== undefined ? lockedStyle : { backgroundColor: "#27ae60", color: "#fff" },
        title: lockedTitle || "Invoiced — cannot change",
      };
    }
    const disabledStyle = isAllowed && !isAllowed(dateStr) ? { color: "#ccc" } : {};
    const base = isAllowed && !isAllowed(dateStr) ? { disabled: true } : {};
    return {
      ...base,
      style: { userSelect: "none", WebkitUserSelect: "none", ...disabledStyle },
      onMouseDown: (e) => { if (e.shiftKey) e.preventDefault(); },
      onClick: (e) => {
        if (e.shiftKey && anchorRef.current) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof window !== "undefined" && window.getSelection) {
            window.getSelection().removeAllRanges();
          }
          const range = expandDateRange(anchorRef.current, dateStr);
          const filtered = isAllowed ? range.filter(isAllowed) : range;
          const merged = Array.from(new Set([...(getValues() || []), ...filtered])).sort();
          setValues(merged);
        } else {
          anchorRef.current = dateStr;
        }
      },
    };
  };

// How many days are in a given calendar month (month1based: 1-12).
export const daysInMonth = (year, month1based) => new Date(year, month1based, 0).getDate();

// All YYYY-MM-DD strings for every day in one calendar month.
export const expandMonthToDays = (year, month1based) => {
  const total = daysInMonth(year, month1based);
  const mm = String(month1based).padStart(2, '0');
  const out = [];
  for (let day = 1; day <= total; day++) {
    out.push(`${year}-${mm}-${String(day).padStart(2, '0')}`);
  }
  return out;
};

// Deduped, sorted union of every day across a set of react-multi-date-picker month
// objects (each with .year and .month.number, 1-based).
export const expandMonthsToDays = (monthObjects) => {
  const all = (monthObjects || []).flatMap((m) => expandMonthToDays(m.year, m.month.number));
  return Array.from(new Set(all)).sort();
};

// Reconstructs which calendar months are "selected" from a stored comma-joined
// tentativeDates string, so the Manmonth month-picker can re-show its chips on edit.
// Returns plain Date objects (first of each distinct YYYY-MM found), which
// react-multi-date-picker's onlyMonthPicker accepts as a `value`.
export const deriveSelectedMonthsFromDates = (tentativeDatesStr) => {
  const dates = tentativeDatesStr ? tentativeDatesStr.split(',').map((d) => d.trim()).filter(Boolean) : [];
  const seen = new Set();
  const months = [];
  dates.forEach((d) => {
    const prefix = d.slice(0, 7); // YYYY-MM
    if (prefix.length === 7 && !seen.has(prefix)) {
      seen.add(prefix);
      months.push(new Date(`${prefix}-01`));
    }
  });
  return months;
};

// Number of distinct calendar months (YYYY-MM) represented among a list of
// YYYY-MM-DD date strings — an informational figure only (see proratedMonthlyAmount
// below for the actual billing formula, which doesn't multiply by this).
export const countDistinctMonths = (dateStrings) => {
  const set = new Set((dateStrings || []).filter(Boolean).map((d) => d.slice(0, 7)));
  return set.size;
};

// Groups a list of YYYY-MM-DD date strings by calendar month, e.g. for a breakdown
// table — one row per month actually touched, each knowing how many days that
// specific month has (28-31, so a day's worth of a monthly rate isn't constant).
// Returns entries sorted chronologically: [{ ym: 'YYYY-MM', year, month, count, daysInMonth }].
export const groupDatesByMonth = (dateStrings) => {
  const byMonth = new Map();
  (dateStrings || []).forEach((d) => {
    if (!d) return;
    const ym = d.slice(0, 7);
    byMonth.set(ym, (byMonth.get(ym) || 0) + 1);
  });
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ym, count]) => {
      const [year, month] = ym.split('-').map(Number);
      return { ym, year, month, count, daysInMonth: daysInMonth(year, month) };
    });
};

// Manmonth's actual billing formula, for splitting ONE month's rate across multiple
// invoices: for each calendar month present in `coveredDates` (e.g. one invoice's
// datesCovered), the rate is divided by that month's user-entered "working days" figure
// (`workingDaysByMonth`, e.g. { '2026-03': 26 } — set per work order in Billing Details,
// since it varies month to month with weekends/holidays), then multiplied by however
// many of those days `coveredDates` accounts for. So a month configured for 26 working
// days, invoiced for 15 of them, gets 15/26 of that month's rate. A month with no
// working-days figure entered yet falls back to the old behavior — dividing by however
// many days were actually assigned that month for THIS work order (`assignedDates`,
// not the calendar month's fixed 28-31 days), which resolves to the full monthlyRate
// per month touched when `coveredDates`/`assignedDates` are the same list.
export const monthlyShareAmount = (coveredDates, assignedDates, monthlyRate, workingDaysByMonth = {}) => {
  const rate = parseFloat(monthlyRate) || 0;
  const assignedByMonth = new Map(groupDatesByMonth(assignedDates).map((g) => [g.ym, g.count]));
  return groupDatesByMonth(coveredDates).reduce((sum, g) => {
    const workingDays = parseFloat(workingDaysByMonth?.[g.ym]);
    const divisor = workingDays > 0 ? workingDays : (assignedByMonth.get(g.ym) || g.count); // fall back to assigned-day count if unknown
    return sum + (divisor > 0 ? (rate / divisor) * g.count : 0);
  }, 0);
};
